import { Component, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { ValidationErrors, FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import {
  BackendError,
  Breakpoint,
  BreakpointState,
  HrefNavigationEvent,
  MediaQueryService,
  ValueValidator,
  AutoSuggestSymbol,
  BuySellService,
  IfFeatureService,
  SecuritySymbol,
  DataAnalyticsTaggingConstants,
  DataAnalyticsTaggingService,
  AnalyticsContextComponent,
  IfFeatureDirective,
  IfNoFeatureDirective,
  ModalPanelComponent,
  ModalHeaderComponent,
  ModalHeaderTitleComponent,
  ModalHeaderButtonsComponent,
  ModalHeaderCloseButtonComponent,
  ModalCloseDirective,
  IconComponent,
  FormComponent,
  FormRowComponent,
  InputGroupComponent,
  InputGroupFieldComponent,
  ClearFieldComponent,
  InputDirective,
  FocusDirective,
  InputValidator,
  InputErrorsComponent,
  IconButtonComponent,
  StickyObserverComponent,
  DividerComponent,
  HrefDirective,
  FocusTrackerDirective,
  FocusTrackerItemDirective,
  ExpandIconComponent,
  TickerSymbolComponent,
  ButtonComponent,
  DetailedLoadingIndicatorComponent,
  TranslatePipe
} from '@tdwb/core';
import { DetailedQuoteComponent } from '@tdwb/webbroker/core';
import { SiteWideSearchRestService } from './site-wide-search-rest.service';
import { RecentSearchItem, SearchResult, SearchResultType } from './site-wide-search.domain';

/**
 * Site Wide Search Component
 *
 * This is the component that creates a site wide search on the page
 */
@Component({
  selector: 'td-wb-site-wide-search',
  templateUrl: 'site-wide-search.component.html',
  styleUrls: ['site-wide-search.component.scss'],
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    TranslateDirective,
    AnalyticsContextComponent,
    IfFeatureDirective,
    IfNoFeatureDirective,
    ModalPanelComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderCloseButtonComponent,
    IconComponent,
    FormComponent,
    FormRowComponent,
    InputGroupComponent,
    InputGroupFieldComponent,
    ClearFieldComponent,
    InputDirective,
    FocusDirective,
    InputValidator,
    InputErrorsComponent,
    IconButtonComponent,
    StickyObserverComponent,
    DividerComponent,
    HrefDirective,
    FocusTrackerDirective,
    FocusTrackerItemDirective,
    ExpandIconComponent,
    TickerSymbolComponent,
    DetailedQuoteComponent,
    ButtonComponent,
    DetailedLoadingIndicatorComponent,
    TranslatePipe,
    ModalCloseDirective
  ],
  providers: [
    SiteWideSearchRestService
  ]
})
export class SiteWideSearchComponent {
  private static readonly DEBOUNCE_TIME: number = 500;

  searchInput: string;
  protected readonly searchResult = signal<SearchResult | undefined>(undefined);
  protected readonly isLoading = signal(false);
  previousButtonClickInput: string;
  protected readonly smallOrMediumScreen = signal(false);
  protected readonly smallScreen = signal(false);
  stickyState: boolean = false;
  protected readonly recentSearchResults = signal<Array<RecentSearchItem>>([]);
  previousSearchTerm: string;
  protected readonly hasFocus = signal(false);
  recentSearchSymbolsEnabled: boolean = false;
  recentSearchResultsUpToDate: boolean = false;

  protected readonly searchInputField = viewChild<ElementRef<HTMLInputElement>>('searchInputField');

  readonly allowableCharactersRegex: RegExp;
  readonly leadingSpecialCharactersRegex: RegExp;

  private readonly searchInputValueChange: Subject<string> = new Subject<string>();
  private lastExpandedRow: number;

  private readonly siteWideSearchRestService = inject(SiteWideSearchRestService);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly featureService = inject(IfFeatureService);
  private readonly buySellService = inject(BuySellService);
  private readonly dataAnalyticsTaggingService = inject(DataAnalyticsTaggingService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        this.smallOrMediumScreen.set(breakpointState.matches);
        this.smallScreen.set(breakpointState.isBreakpointMatch(Breakpoint.SM));
      });

    this.setupSearchInputValueChangeObserver();

    // For new universal search we are allowing an extra '.' and also do recent searches call
    if (this.getUniversalSearchFeatureFlag()) {
      this.allowableCharactersRegex = new RegExp('^[a-zA-Z0-9&$?,.â€™ \'\u00C0-\u00FF]*$');
      this.leadingSpecialCharactersRegex = new RegExp('^[&$?,.â€™ \'][a-zA-Z0-9&$?,.â€™ \'\u00C0-\u00FF]*$');
      this.recentSearchSymbolsEnabled = this.featureService.getFeatureFlag<boolean>('universalSearchRecentSearchSymbols');
      this.getRecentSearches();
    } else {
      this.allowableCharactersRegex = new RegExp('^[a-zA-Z0-9&$?,â€™ \'\u00C0-\u00FF]*$');
      this.leadingSpecialCharactersRegex = new RegExp('^[&$?,â€™ \'][a-zA-Z0-9&$?,â€™ \'\u00C0-\u00FF]*$');
    }
  }

  setSearchQuery(symbolQuery: string): void {
    if (symbolQuery !== undefined && !this.hasError(symbolQuery)) {
      if (symbolQuery !== this.previousSearchTerm) {
        this.isLoading.set(true);
        this.searchInputValueChange.next(symbolQuery);
        this.previousSearchTerm = symbolQuery;
      }
    } else {
      this.searchResult.set(undefined);
    }
  }

  inputChange(inputValue: string): void {
    this.previousButtonClickInput = undefined; // reset button click check when user types
    this.lastExpandedRow = undefined;

    if (inputValue === undefined || inputValue === '') {
      this.dataAnalyticsTaggingService
        .emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData('td:wb:search:clear:result'));
      this.searchResult.set(undefined);
      this.previousSearchTerm = undefined;
      if (this.getUniversalSearchFeatureFlag()) {
        this.getRecentSearches();
      }
    } else {
      this.setSearchQuery(inputValue);
    }
  }

  onSearchButtonClicked(): void {
    if (this.searchInput === undefined || this.searchInput === '') {
      this.searchResult.set(undefined);
    } else if (this.previousButtonClickInput !== this.searchInput) {
      if (this.getUniversalSearchFeatureFlag() && !this.isInputValueInvalid(this.searchInput)) {
        this.setRecentSearches(this.searchInput);
      }

      // above check is to ensure we're not sending repeated requests with the same entered search input
      // if users click multiple times on search button
      this.setSearchQuery(this.searchInput);
      this.previousButtonClickInput = this.searchInput;
    }
  }

  displayResults(): boolean {
    return this.isNotEmptyInputValue();
  }

  displayTextSnippet(i: number): boolean {
    return !(this.smallOrMediumScreen() && i >= 1);
  }

  displayHelpResults(): boolean {
    const result = this.searchResult();
    return result !== undefined && result.help !== undefined && result.help.length > 0
      && !this.hasError(this.searchInput);
  }

  displaySymbolResults(): boolean {
    const result = this.searchResult();
    return result !== undefined && result.symbol !== undefined && result.symbol.length > 0
      && !this.hasError(this.searchInput);
  }

  displayEducationResults(): boolean {
    const result = this.searchResult();
    return result !== undefined && result.education !== undefined && result.education.length > 0
      && !this.hasError(this.searchInput);
  }

  displayNoResultsFound(): boolean {
    return !this.displayHelpResults() && !this.displayEducationResults() && !this.displaySymbolResults() || this.hasError(this.searchInput);
  }

  stickyStateChange(stickyState: boolean): void {
    this.stickyState = stickyState;
  }

  searchValueExists(): void {
    if (this.isInputValueInvalid(this.searchInputField()?.nativeElement.value)) {
      return;
    } else {
      this.setRecentSearch();
    }
  }

  setRecentSearch(value?: string): false {
    const index = this.recentSearchResults().find((result: RecentSearchItem) => {
      return result.searchText === value;
    })?.priority;

    if (index !== undefined) {
      this.dataAnalyticsTaggingService.emitTaggingEvent(
        DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:recent:search:${index - 1}`));
    }

    if (value) {
      this.searchInput = value;
    } else {
      this.searchInput = this.searchInputField()?.nativeElement.value;
    }

    this.setRecentSearches(this.searchInput);
    this.inputChange(this.searchInput);
    return false;
  }

  clearRecentSearches(recentSearchItem?: RecentSearchItem): void {
    const allRecentSearches = this.recentSearchResults().map((obj: RecentSearchItem) => ({
      searchText: obj.searchText,
      market: obj.market,
      symbolType: obj.symbolType
    }));

    if (recentSearchItem === undefined) {
      this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData('td:wb:search:clear:recent:searches'));
      this.siteWideSearchRestService.deleteRecentSearch(allRecentSearches)
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.recentSearchResults.set([]);
      this.hasFocus.set(true);
    } else {
      this.siteWideSearchRestService.deleteRecentSearch([{
        searchText: recentSearchItem.searchText,
        market: recentSearchItem.market,
        symbolType: recentSearchItem.symbolType
      }])
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

      const index = allRecentSearches.findIndex((item: RecentSearchItem): boolean =>
        item.searchText === recentSearchItem.searchText && item.market === recentSearchItem.market);

      if (index !== -1) {
        this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:clear:result:${index}`));
        if (index === this.lastExpandedRow) {
          this.lastExpandedRow = undefined;
        } else if (index < this.lastExpandedRow) {
          this.lastExpandedRow = this.lastExpandedRow - 1;
        }
        this.recentSearchResults.update((items: Array<RecentSearchItem>) => items.filter((_: RecentSearchItem, i: number): boolean => i !== index));
      }
    }

    if (this.recentSearchResults().length === 0) {
      this.hasFocus.set(true);
    }
  }

  isHelpDisplayedFirst(): boolean {
    const result = this.searchResult();
    return result !== undefined && result.categoryOrder !== undefined &&
      result.categoryOrder instanceof Array && result.categoryOrder.length > 0 &&
      result.categoryOrder[0] === SearchResultType.HELP;
  }

  hasError(searchInput: string): boolean {
    return searchInput !== undefined && searchInput !== '' &&
      (searchInput?.match(this.allowableCharactersRegex) === null ||
        searchInput?.match(this.leadingSpecialCharactersRegex) !== null);
  }

  createSearchValidator(): ValueValidator {
    return (): ValidationErrors | null => this.hasLeadingSpecialCharacters();
  }

  isRowExpanded(index: number): boolean {
    return index === this.lastExpandedRow;
  }

  toggleExpandRow(index: number): false {
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:stock:${index}`));

    if (index === this.lastExpandedRow) {
      this.lastExpandedRow = undefined;
    } else {
      this.lastExpandedRow = index;
      if (this.recentSearchSymbolsEnabled) {
        const searchResult = this.searchResult();
        const symbolSearchItem = searchResult?.symbol[index];
        if (symbolSearchItem !== undefined) {
          this.setRecentSymbolSelection({
            searchText: symbolSearchItem.symbol,
            market: symbolSearchItem.marketType,
            symbolType: symbolSearchItem.symbolType
          });
        }
      } else {
        this.setRecentSearches(this.searchInputField()?.nativeElement.value);
      }
    }

    return false;
  }

  toggleExpandRecentSearchRow(index: number): false {
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:search:recent:search:${index}`));

    if (index === this.lastExpandedRow) {
      this.lastExpandedRow = undefined;
    } else {
      this.lastExpandedRow = index;
    }

    return false;
  }

  getSymbol(symbol: AutoSuggestSymbol): Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'> {
    return {
      tickerSymbol: symbol.symbol,
      type: symbol.symbolType.toString(),
      market: symbol.marketType
    } as Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'>;
  }

  getSymbolFromRecentSearch(symbol: RecentSearchItem): Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'> {
    return {
      tickerSymbol: symbol.searchText,
      type: symbol.symbolType.toString(),
      market: symbol.market
    } as Pick<SecuritySymbol, 'tickerSymbol' | 'type' | 'market'>;
  }

  setRecentSearchesAndNavigate(ref: HrefNavigationEvent, index: number): void {
    const searchSection = ref.ref.includes('help') ? 'helpful:articles' : 'video:lessons';
    this.dataAnalyticsTaggingService.emitTaggingEvent(
      DataAnalyticsTaggingConstants.getLinkClickEventData(`td:wb:${searchSection}:${index}`));
    this.setRecentSearches(this.searchInputField()?.nativeElement.value);
    if (ref !== undefined) {
      ref.navigate();
    }
  }

  isBuySellDisabled(symbol: AutoSuggestSymbol): boolean {
    return !this.buySellService.hasPermission(symbol.symbolType);
  }

  showRecentSearchSymbol(recentSearchItem: RecentSearchItem): boolean {
    return this.recentSearchSymbolsEnabled && recentSearchItem.market !== undefined;
  }

  private hasLeadingSpecialCharacters(): ValidationErrors | null {
    if (this.searchInput !== '' && this.searchInput?.match(this.leadingSpecialCharactersRegex) !== null) {
      return { special: true };
    }

    return null;
  }

  private setupSearchInputValueChangeObserver(): void {
    this.searchInputValueChange
      .pipe(
        debounceTime(SiteWideSearchComponent.DEBOUNCE_TIME),
        switchMap((searchQuery: string): Observable<SearchResult> => this.doSearch(searchQuery)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (result: SearchResult): void => {
          this.searchResult.set(result);
          this.lastExpandedRow = undefined;
          this.isLoading.set(false);
          this.dataAnalyticsTaggingService.emitTaggingEvent(DataAnalyticsTaggingConstants.getSearchEventData(this.searchInput, this.getSearchResultCount(result).toString()));
        },
        error: (error: BackendError): void => {
          // reset button click check on error in case user wants to press button to try again
          this.previousButtonClickInput = undefined;
          this.setupSearchInputValueChangeObserver();
          throw error;
        }
      });
  }

  private getSearchResultCount(result: SearchResult): number {
    return (result.help?.length ?? 0) +
      (result.education?.length ?? 0) +
      (result.symbol?.length ?? 0);
  }

  private getRecentSearches(): void {
    if (!this.recentSearchResultsUpToDate) {
      // Set here to ensure multiple calls are not placed at the same time to update
      this.recentSearchResultsUpToDate = true;
      this.siteWideSearchRestService.getRecentSearches()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: Array<RecentSearchItem>): void => {
            if (result.length > 0) {
              this.recentSearchResults.set(result);
            }
          },
          error: (errorResponse: Array<BackendError>): void => {
            if (errorResponse && errorResponse !== undefined) {
              this.recentSearchResults.set([]);
              this.recentSearchResultsUpToDate = false;
            }
          }
        });
    }
  }

  private setRecentSearches(searchText: string): void {
    this.siteWideSearchRestService.setRecentSearch(searchText)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.recentSearchResultsUpToDate = false;
  }

  private setRecentSymbolSelection(symbolSearchItem: RecentSearchItem): void {
    this.siteWideSearchRestService.setRecentSymbolSearch(symbolSearchItem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.recentSearchResultsUpToDate = false;
  }

  private doSearch(searchQuery: string): Observable<SearchResult> {
    return this.siteWideSearchRestService.search(searchQuery);
  }

  private getUniversalSearchFeatureFlag(): boolean {
    return this.featureService.getFeatureFlag<boolean>('universalSearch');
  }

  private isNotEmptyInputValue(): boolean {
    return this.searchInput !== null && this.searchInput !== undefined && this.searchInput !== '';
  }

  private isInputValueInvalid(inputString: string): boolean {
    return inputString === undefined ||
      inputString === '' ||
      inputString?.match(this.allowableCharactersRegex) === null ||
      inputString?.match(this.leadingSpecialCharactersRegex) !== null;
  }
}
